import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, InputNumber, Modal } from 'antd'
import { store, postPwr, requestMyActivities, saveLiveActivity, startWork, assertDaemonCompatible } from './store/store'
import { getLocalStorageValue, setLocalStorageValue } from './store/localstorage'
import { Modes } from './store/sensors'
import { loadCapture, saveCapture, clearCapture, buildCapture } from './workoutCapture'
import { renderPlayerChart, niceYMax } from './playerChart'
import { showDaemonBlockModal } from './DaemonBlockUi'
import {
    workoutTargetSeries, decimateSeries, intervalRemaining, averagePower3s, classifySensorState,
    currentMetric, targetWatts, percentOfFtp, powerZoneLabel, hrZoneLabel,
    formatWatts, formatHeartRate, formatCadence, formatPercent, FTP_KEY,
    pushCadenceSample, cadenceStopped, cadenceResuming,
} from './playerLogic'
import { formatDuration as formatDurationFallback } from './workoutLogic'
import './css/training-player.css'

const MAX_HR_KEY = 'trainer/maxHr'

function SensorSemaphore({ state }) {
    return <span className={`sensor-semaphore sensor-semaphore--${state}`} aria-hidden="true" />
}

function PlayerStat({ label, value }) {
    return (
        <div className="player-stat">
            <span className="player-stat__label">{label}</span>
            <span className="player-stat__value">{value}</span>
        </div>
    )
}

function PlayerPowerChartInner({ targets, liveData, elapsedSeconds, ftpWatts }) {
    // Overtime extends the x-domain past the planned target profile so the
    // current-time marker and the live line stay visible beyond the plan.
    const duration = Math.max(
        targets && targets.length ? targets[targets.length - 1].time : 0,
        elapsedSeconds,
    )

    // Cap the display resolution (~2 trends, 2400 pts each) so the canvas
    // redraw stays cheap on every 1 s tick and every resize; the stepped
    // profile survives sampling unchanged.
    const targetData = decimateSeries((targets || []).map(p => ({ time: p.time, watts: p.targetWatts })))
    const liveDatum = decimateSeries((liveData || []).filter(p => p.currentWatts != null)
        .map(p => ({ time: p.time, watts: p.currentWatts })))

    let peak = ftpWatts != null ? ftpWatts : 0
    targetData.forEach(p => { if (p.watts > peak) peak = p.watts })
    liveDatum.forEach(p => { if (p.watts > peak) peak = p.watts })
    const yMax = niceYMax(peak)

    const wrapRef = useRef(null)
    const canvasRef = useRef(null)

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        renderPlayerChart(canvas, {
            targetData,
            liveDatum,
            elapsedSeconds,
            ftpWatts,
            duration,
            yMax,
        })
    }, [targetData, liveDatum, elapsedSeconds, ftpWatts, duration, yMax])

    // Redraw whenever the rendering inputs change.
    useEffect(() => { draw() }, [draw])

    // The canvas re-measures itself from the container, so a resize is just
    // another redraw (milliseconds) — no chart re-render, no remount.
    const drawRef = useRef(draw)
    drawRef.current = draw
    useEffect(() => {
        const el = wrapRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(() => {
            if (drawRef.current) drawRef.current()
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    if (targetData.length === 0) return <div className="training-player__chart" ref={wrapRef} />

    return (
        <div className="training-player__chart" ref={wrapRef}>
            <canvas className="training-player__chart-canvas" ref={canvasRef} />
        </div>
    )
}

const PlayerPowerChart = memo(PlayerPowerChartInner)

function LayoutTraining({ workout, ftp: initialFtp, onNormalMode }) {
    const sensors = useSelector(state => state.sensors)
    const saveStatus = useSelector(state => state.workouts.saveStatus)

    const [playerState, setPlayerState] = useState('ready')
    const [ftp, setFtpState] = useState(initialFtp || getLocalStorageValue(FTP_KEY, 200) || 200)
    const [maxHr, setMaxHr] = useState(getLocalStorageValue(MAX_HR_KEY, null))
    const [elapsed, setElapsed] = useState(0)
    const [series, setSeries] = useState([])
    const [pwrRing, setPwrRing] = useState([])
    const [autoPause, setAutoPause] = useState(false)
    const [lastGoodAt, setLastGoodAt] = useState(0)
    const [hardwareError, setHardwareError] = useState(undefined)
    const [saveError, setSaveError] = useState(undefined)
    const [recoverable, setRecoverable] = useState(false)
    const [captureWarning, setCaptureWarning] = useState(false)

    const elapsedRef = useRef(0)
    const seriesRef = useRef([])
    const samplesRef = useRef([])
    const eventsRef = useRef([])
    const pwrRingRef = useRef([])
    const lastGoodAtRef = useRef(0)
    const lastDispatchedRef = useRef(null)
    const startTimeRef = useRef(null)
    const pwrWarmupRef = useRef(true)
    const ftpRef = useRef(ftp)
    const targetSeriesRef = useRef([])
    const workStartedRef = useRef(false)
    const playerStateRef = useRef(playerState)
    const cadenceRingRef = useRef([])
    const autoPausedRef = useRef(false)
    const phaseRef = useRef('running')
    // Read once at mount: a pending capture makes the player land in the
    // stop-review with recovery controls instead of silently discarding it.
    const recoveryRef = useRef(loadCapture())
    const recoveryDoneRef = useRef(false)

    ftpRef.current = ftp

    useEffect(() => { playerStateRef.current = playerState })

    const recalculated = useMemo(() => workoutTargetSeries(workout.points, ftp), [workout, ftp])
    targetSeriesRef.current = recalculated
    const duration = recalculated.length

    useEffect(() => { setLocalStorageValue(FTP_KEY, ftp) }, [ftp])
    useEffect(() => { setLocalStorageValue(MAX_HR_KEY, maxHr) }, [maxHr])

    // Entering the player must not depend on a prior visit to the config page:
    // re-apply the saved (preconfigured) sensor selection when the sensors
    // store has not been started yet (e.g. the app reloaded straight into the
    // training view). startWork() re-applies localStorage selection or scans.
    // The ref keeps this one-shot per mount: React StrictMode double-invokes
    // effects in dev, and each startWork() POST is serialized behind the
    // daemon's single sensor mutex (measured at ~12 s per call), so a second
    // one would stall the first ticks of the workout by the same amount again.
    useEffect(() => {
        if (sensors.mode === Modes.none && !workStartedRef.current && !recoveryRef.current) {
            workStartedRef.current = true
            startWork()
        }
    }, [sensors.mode])

    const pushEvent = useCallback((event, action) => {
        const ev = { timestamp: elapsedRef.current, event, action }
        eventsRef.current = [...eventsRef.current, ev]
    }, [])

    const dispatchTargetNow = useCallback(() => {
        const s = targetSeriesRef.current
        const idx = Math.min(elapsedRef.current, Math.max(0, s.length - 1))
        const pct = s.length ? s[idx].pct : 0
        const target = targetWatts(pct, ftpRef.current)
        if (target !== lastDispatchedRef.current) {
            lastDispatchedRef.current = target
            postPwr({ pwr: target }).catch(() => setHardwareError('Тренажёр недоступен'))
        }
        return target
    }, [])

    const persist = useCallback(() => {
        const cap = buildCapture({
            workout,
            ftp,
            startTime: startTimeRef.current,
            samples: samplesRef.current,
            events: eventsRef.current,
            elapsed: elapsedRef.current,
            timer: Math.min(elapsedRef.current, duration),
            phase: phaseRef.current,
        })
        if (!saveCapture(cap)) {
            setCaptureWarning(true)
        }
    }, [workout, ftp, duration])

    const startTimer = useCallback(async () => {
        // Fresh per-click gate: a re-blocked or swapped daemon must never let a
        // workout start on a cached (stale) verdict.
        const verdict = await assertDaemonCompatible()
        if (!verdict.ok) {
            showDaemonBlockModal(verdict)
            return
        }
        if (startTimeRef.current == null) {
            startTimeRef.current = Math.floor(Date.now() / 1000)
        }
        cadenceRingRef.current = []
        lastDispatchedRef.current = null
        lastDispatchedRef.current = dispatchTargetNow()
        phaseRef.current = 'running'
        setPlayerState('running')
        pushEvent('start', 'start')
    }, [dispatchTargetNow, pushEvent])

    const onPause = useCallback((action = 'pause') => {
        autoPausedRef.current = action === 'auto'
        setAutoPause(action === 'auto')
        phaseRef.current = 'paused'
        setPlayerState('paused')
        pushEvent('pause', action)
        if (action === 'auto') {
            // The rider stopped, so the trainer broadcasts ~0 W. Leaving the
            // session setpoint active lets the daemon's power-match PID keep
            // integrating error = setpoint - 0 and ramp its ERG command while
            // nobody pedals. Dispatch a 0 W setpoint so the trainer freewheels
            // and the PID window settles to zero during the pause; resume
            // re-dispatches the current target afterwards.
            postPwr({ pwr: 0 }).catch(() => setHardwareError('Тренажёр недоступен'))
        }
        persist()
    }, [pushEvent, persist])

    const onResume = useCallback((action = 'resume') => {
        autoPausedRef.current = false
        setAutoPause(false)
        phaseRef.current = elapsedRef.current > duration ? 'overtime' : 'running'
        setPlayerState('running')
        lastDispatchedRef.current = null
        lastDispatchedRef.current = dispatchTargetNow()
        pushEvent('resume', action)
        persist()
    }, [dispatchTargetNow, pushEvent, persist, duration])

    const tick = useCallback(() => {
        if (playerStateRef.current !== 'running' && playerStateRef.current !== 'overtime') return

        const next = elapsedRef.current + 1

        const sensorStateNow = store.getState().sensors
        const pwr = currentMetric(sensorStateNow.data, 'pwr')
        const hr = currentMetric(sensorStateNow.data, 'hr')
        const cad = currentMetric(sensorStateNow.data, 'cad')

        cadenceRingRef.current = pushCadenceSample(cadenceRingRef.current, cad)

        if (cadenceStopped(cadenceRingRef.current)) {
            onPause('auto')
            return
        }

        elapsedRef.current = next

        if (pwr != null || hr != null) {
            lastGoodAtRef.current = Date.now()
            setLastGoodAt(Date.now())
        }

        const ring = pwr != null ? [...pwrRingRef.current, pwr].slice(-3) : pwrRingRef.current
        pwrRingRef.current = ring
        setPwrRing(ring)

        const s = targetSeriesRef.current
        const pct = s.length ? s[Math.min(next, s.length - 1)].pct : null
        const target = pct != null ? targetWatts(pct, ftpRef.current) : null

        // The first tick snaps whatever the trainer was broadcasting before the
        // run (pre-control value, often far from the just-dispatched target →
        // a lone high dot). Skip that warmup sample so the live line starts at
        // the first power actually ridden under the new target.
        const warmup = pwrWarmupRef.current && pwr != null
        pwrWarmupRef.current = false
        if (pwr != null && !warmup) {
            const point = { time: next, currentWatts: pwr }
            seriesRef.current = [...seriesRef.current, point]
            setSeries(seriesRef.current)
        }

        const sample = { timestamp: next, power: pwr != null ? pwr : null, hr: hr != null ? hr : null, cadence: cad != null ? cad : null, target_power: target }
        samplesRef.current = [...samplesRef.current, sample]

        setElapsed(next)

        if (target != null && target !== lastDispatchedRef.current) {
            lastDispatchedRef.current = target
            postPwr({ pwr: target }).catch(() => setHardwareError('Тренажёр недоступен'))
        }

        if (next >= duration && phaseRef.current === 'running') {
            phaseRef.current = 'overtime'
        }

        persist()
    }, [duration, onPause, persist])

    useEffect(() => {
        if (playerState !== 'running' && playerState !== 'overtime') return
        const id = setInterval(tick, 1000)
        return () => clearInterval(id)
    }, [playerState, tick])

    // While an automatic (cadence-driven) pause holds, keep sampling cadence
    // once per second and resume as soon as it starts growing again — the
    // athlete began pedalling. Manual pauses arm no such watcher.
    useEffect(() => {
        if (playerState !== 'paused' || !autoPausedRef.current) return
        const id = setInterval(() => {
            const state = store.getState().sensors
            cadenceRingRef.current = pushCadenceSample(cadenceRingRef.current, currentMetric(state.data, 'cad'))
            if (cadenceResuming(cadenceRingRef.current)) {
                onResume('auto')
            }
        }, 1000)
        return () => clearInterval(id)
    }, [playerState, onResume])

    // Track data freshness from live sensor updates too, so the status stays
    // "Датчики активны" on the ready screen instead of reporting stale data
    // while the timer has not started. The tick loop only stamps lastGoodAt
    // during a run.
    useEffect(() => {
        const pwr = currentMetric(sensors.data, 'pwr')
        const hr = currentMetric(sensors.data, 'hr')
        const cad = currentMetric(sensors.data, 'cad')
        if (pwr != null || hr != null || cad != null) {
            lastGoodAtRef.current = Date.now()
            setLastGoodAt(Date.now())
        }
    }, [sensors])

    useEffect(() => {
        return () => {
            lastDispatchedRef.current = null
        }
    }, [])

    useEffect(() => {
        // A captured (interrupted) session lands in the stop-review with the
        // recording clock resumed from the captured second. 'stopped' captures
        // are not resumable — only save or discard is offered.
        const cap = recoveryRef.current
        if (!cap || recoveryDoneRef.current) return
        recoveryDoneRef.current = true

        samplesRef.current = cap.samples || []
        eventsRef.current = cap.events || []
        seriesRef.current = (cap.samples || [])
            .filter(s => s.power != null)
            .map(s => ({ time: s.timestamp, currentWatts: s.power }))
        setSeries(seriesRef.current)
        startTimeRef.current = cap.start_time
        const capturedElapsed = typeof cap.elapsed === 'number' ? cap.elapsed : 0
        elapsedRef.current = capturedElapsed
        setElapsed(capturedElapsed)
        phaseRef.current = ['running', 'overtime', 'paused'].includes(cap.phase)
            ? cap.phase
            : 'running'
        const resumable = cap.phase !== 'stopped'
        setRecoverable(resumable)
        // No stale erg setpoint is re-sent on recovery; resume re-dispatches.
        lastDispatchedRef.current = null

        if (resumable && (cap.phase === 'running' || cap.phase === 'overtime')) {
            pushEvent('pause', 'recover')
            persist()
        }

        setPlayerState('stopped')
    }, [persist, pushEvent])

    const onStop = useCallback(() => {
        phaseRef.current = 'stopped'
        setRecoverable(false)
        setPlayerState('stopped')
        pushEvent('stop', 'stop')
        persist()
    }, [pushEvent, persist])

    const onSave = useCallback(async () => {
        if (saveStatus === 'saving') return
        if (!samplesRef.current.length && !eventsRef.current.length) {
            onNormalMode()
            return
        }
        setSaveError(undefined)
        const payload = {
            workout_id: workout.id,
            workout_name: workout.name,
            ftp,
            start_time: startTimeRef.current,
            elapsed_duration: elapsedRef.current,
            timer_duration: Math.min(elapsedRef.current, duration),
            samples: samplesRef.current,
            events: eventsRef.current,
        }
        try {
            await saveLiveActivity(payload)
            clearCapture()
            requestMyActivities()
            onNormalMode()
        } catch (e) {
            setSaveError((e && e.message) || 'Не удалось сохранить тренировку')
        }
    }, [saveStatus, workout, ftp, duration, onNormalMode])

    const resumeFromCapture = useCallback(async () => {
        const verdict = await assertDaemonCompatible()
        if (!verdict.ok) {
            showDaemonBlockModal(verdict)
            return
        }
        startWork()
        // The first post-resume power reading IS riding data — do not warm it up.
        pwrWarmupRef.current = false
        autoPausedRef.current = false
        if (phaseRef.current === 'paused') {
            setPlayerState('paused')
            return
        }
        setPlayerState('running')
        lastDispatchedRef.current = null
        lastDispatchedRef.current = dispatchTargetNow()
        pushEvent('resume', 'recover')
        persist()
    }, [dispatchTargetNow, pushEvent, persist])

    const onDiscard = useCallback(() => {
        const discard = () => {
            clearCapture()
            onNormalMode()
        }
        if (samplesRef.current.length) {
            Modal.confirm({
                title: 'Отказаться от тренировки?',
                content: 'Записанные данные будут удалены без сохранения.',
                okText: 'Отказаться',
                okType: 'danger',
                cancelText: 'Отмена',
                onOk: discard,
            })
        } else {
            discard()
        }
    }, [onNormalMode])

    const handleExit = useCallback(() => {
        const leave = () => {
            clearCapture()
            onNormalMode()
        }
        const unsaved = samplesRef.current.length > 0 && playerState !== 'ready'
        if (!unsaved) {
            leave()
            return
        }
        Modal.confirm({
            title: 'Выйти в обычный режим?',
            content: 'Незавершённая тренировка будет потеряна.',
            okText: 'Выйти',
            okType: 'danger',
            cancelText: 'Остаться',
            onOk: leave,
        })
    }, [playerState, onNormalMode])

    const pwrNow = currentMetric(sensors.data, 'pwr')
    const hr = currentMetric(sensors.data, 'hr')
    const cad = currentMetric(sensors.data, 'cad')
    // The 3s rolling average is only populated while the timer runs; on the
    // ready screen fall back to the live power so the trainer's value is shown
    // immediately (matching how HR is read live).
    const avg3 = averagePower3s(pwrRing.map(v => ({ power: v })))
    const displayPower = avg3 != null ? avg3 : pwrNow
    const pctCur = percentOfFtp(displayPower, ftp)
    const powerZone = powerZoneLabel(pctCur)
    const hrZone = hrZoneLabel(hr, maxHr)

    const s = targetSeriesRef.current
    const curIdx = Math.min(elapsed, Math.max(0, s.length - 1))
    const targetPower = s.length ? targetWatts(s[curIdx].pct, ftp) : null
    const remaining = intervalRemaining(s, elapsed)

    const sensorState = classifySensorState({
        mode: sensors.mode,
        lastGoodAt,
        now: Date.now(),
    })

    let sensorMessage
    if (playerState === 'paused' || playerState === 'stopped') {
        sensorMessage = 'Тренажёр удерживает последнюю цель'
    } else if (sensorState === 'connected') {
        sensorMessage = 'Датчики активны'
    } else if (sensorState === 'stale') {
        sensorMessage = 'Нет данных с датчиков'
    } else {
        sensorMessage = 'Оборудование не выбрано'
    }

    const isStopReview = playerState === 'stopped'

    let reviewTitle = 'Тренировка остановлена'
    if (recoverable) {
        reviewTitle = 'Тренировка прервана'
    } else if (elapsed >= duration) {
        reviewTitle = 'Тренировка завершена'
    }

    return (
        <main className={`training-player training-player--${playerState}`}>
            <header className="training-player__topbar">
                <Button type="text" className="training-player__back" onClick={handleExit}
                    aria-label="Вернуться в обычный режим">‹</Button>
                <div className="training-player__identity">
                    <strong>Тренировка</strong>
                    <span>{workout.name || 'Без названия'}</span>
                </div>
                <Button type="text" className="training-player__menu" aria-label="Меню тренировки">•••</Button>
            </header>

            <section className="training-player__readouts" aria-label="Текущие показатели">
                <div className="player-readout">
                    <span className="player-readout__label">МОЩНОСТЬ</span>
                    <strong className="player-readout__value">{formatWatts(displayPower)}</strong>
                    <span className="player-readout__meta">{powerZone || 'Зона —'}</span>
                </div>
                <div className="player-readout player-readout--center">
                    <span className="player-readout__label">ИНТЕРВАЛ</span>
                    <strong className="player-readout__value player-readout__value--timer">
                        {formatDurationFallback(remaining)}
                    </strong>
                    <span className="player-readout__meta">до смены цели</span>
                </div>
                <div className="player-readout player-readout--right">
                    <span className="player-readout__label">ПУЛЬС</span>
                    <strong className="player-readout__value">{formatHeartRate(hr)}</strong>
                    <span className="player-readout__meta">{hrZone || 'Зона —'}</span>
                </div>
            </section>

            <section className="training-player__stage" aria-live="polite">
                <div className="training-player__target">
                    <span>ЦЕЛЬ</span>
                    <strong>{formatWatts(targetPower)}</strong>
                    <small>{formatPercent(s[curIdx] ? s[curIdx].pct : null)} FTP</small>
                </div>
                <div className="training-player__cadence">
                    <span>КАДЕНС</span>
                    <strong>{formatCadence(cad)}</strong>
                </div>

                {playerState === 'ready' && (
                    <div className="training-player__state-card">
                        <Button className="training-player__play" shape="circle"
                            aria-label="Начать тренировку" onClick={startTimer}>▶</Button>
                        <strong>Начать тренировку</strong>
                    </div>
                )}

                {playerState === 'running' && (
                    <div className="training-player__control-group">
                        <Button className="training-player__pause" onClick={() => onPause('manual')}>Пауза</Button>
                        <Button danger onClick={onStop}>Стоп</Button>
                    </div>
                )}

                {playerState === 'paused' && (
                    <>
                        {autoPause && (
                            <div className="training-player__auto-note">Каденс 0 — автопауза</div>
                        )}
                        <div className="training-player__control-group">
                            <Button type="primary" onClick={() => onResume('manual')}>Продолжить</Button>
                            <Button danger onClick={onStop}>Стоп</Button>
                        </div>
                    </>
                )}

                {isStopReview && (
                    <div className="training-player__stop-review" aria-live="polite">
                        <strong>{reviewTitle}</strong>
                        <span>{formatDurationFallback(elapsed)} записано</span>
                        {saveError && <span className="training-player__error">{saveError}</span>}
                        {captureWarning && (
                            <span className="training-player__error">
                                Данные не записались в браузер — сохраните тренировку до выхода
                            </span>
                        )}
                        <div className="training-player__control-group">
                            {recoverable && (
                                <Button type="primary" onClick={resumeFromCapture}>Продолжить</Button>
                            )}
                            <Button type="primary" onClick={onSave} disabled={saveStatus === 'saving'}>
                                {saveStatus === 'saving' ? 'Сохранение...' : 'Сохранить'}
                            </Button>
                            <Button danger onClick={onDiscard}>Отказаться</Button>
                        </div>
                    </div>
                )}
            </section>

            <section className="training-player__chart-panel" aria-label="Мощность по времени">
                <div className="training-player__chart-toolbar">
                    <span>ВРЕМЯ ТРЕНИРОВКИ</span>
                    <strong>{formatDurationFallback(elapsed)} / {formatDurationFallback(duration)}</strong>
                    <span className="training-player__ftp-reference">
                        <InputNumber size="small" min={50} max={500} value={ftp}
                            onChange={v => setFtpState(v != null ? v : ftp)}
                            addonAfter="W" aria-label="FTP, ватты" />
                    </span>
                </div>
                <PlayerPowerChart targets={recalculated} liveData={series} elapsedSeconds={elapsed}
                    ftpWatts={ftp} />
            </section>

            <section className="training-player__stats" aria-label="Статистика тренировки">
                <PlayerStat label="ТЕКУЩАЯ МОЩНОСТЬ, 3 С" value={formatWatts(avg3)} />
                <PlayerStat label="ЦЕЛЕВАЯ МОЩНОСТЬ" value={formatWatts(targetPower)} />
                <PlayerStat label="ПУЛЬС" value={formatHeartRate(hr)} />
                <PlayerStat label="POWER ZONE" value={powerZone || '—'} />
                <PlayerStat label="HR ZONE" value={hrZone || '—'} />
            </section>

            <footer className="training-player__footer">
                <SensorSemaphore state={sensorState} />
                <span>{hardwareError || sensorMessage}</span>
                <span className="training-player__footer-spacer" />
                <span aria-label="Максимальный пульс">
                    ПМП <InputNumber size="small" min={60} max={260} value={maxHr}
                        onChange={v => setMaxHr(v)} placeholder="—" addonAfter="уд/мин" />
                </span>
                <Button type="text" onClick={handleExit}>В обычный режим</Button>
            </footer>
        </main>
    )
}

export default LayoutTraining
